import type {
    ConfigSetting,
    MoneyJson,
    Product,
    SchedulingMethod,
} from '../types/products.types';
import type { ConfigOptionRecord } from '@/features/smr-admin/services/smr-admin';
import { parseProductFees } from './productFeeTransform';

/**
 * Raw config setting shape as serialized by the backend.
 * Laravel serializes the `configOption()` BelongsTo relation as snake_case
 * (`config_option`) — the frontend types and components expect camelCase
 * (`configOption`), so we normalize at this boundary.
 */
export interface RawConfigSetting {
    id?: string;
    product_id?: string;
    config_option_id?: string | null;
    key: string;
    value: unknown;
    config_option?: ConfigOptionRecord | null;
    configOption?: ConfigOptionRecord | null;
    created_at?: string;
    updated_at?: string;
}

export interface RawApiProduct {
    config_settings?: RawConfigSetting[];
    id: string;
    // Appended accessor on the backend Product model (#474). Snake_case on
    // the wire; normalized to `Product.schedulingMethods` below.
    scheduling_methods?: SchedulingMethod[];
    product_fees?: unknown;
    // Read-only id of the managed system plan; the `...apiProduct` spread in
    // transformApiProduct already passes this through untransformed.
    system_plan_id?: string | null;
    effective_from?: string | null;
    [key: string]: unknown;
}

/**
 * Upper bound (inclusive) on `collection_lead_months`: the whole months by
 * which the DD collection window must finish before the final service falls
 * due. A value above this is treated as UNSET (0 = today's behaviour) rather
 * than clamped — see `parseCollectionLeadMonths` below.
 *
 * MUST be kept in step with ConfigOptionSeeder's validation_rules['max'] for
 * `collection_lead_months` (currently 6) — see docs/features/product-config-options.md.
 */
export const MAX_COLLECTION_LEAD_MONTHS = 6;

/** Client half of the seeder ceiling shared by the `*_collection_instalments` rows.
 *  Widening is a seeder + client edit in ONE PR. */
export const COLLECTION_INSTALMENTS_MAX = 60;

const LEAD_RANGE = { min: 0, max: MAX_COLLECTION_LEAD_MONTHS };
const INSTALMENTS_RANGE = { min: 1, max: COLLECTION_INSTALMENTS_MAX };

/** Ambiguous or malformed input (wrong type, out of range, a duplicate row) resolves
 *  to `undefined` — never coerced, truncated, or clamped into range. */
function parseSingleIntegerSetting(
    settings: ConfigSetting[],
    key: string,
    range: { min: number; max: number },
): number | undefined {
    const rows = settings.filter((s) => s.key === key);
    // product_config_settings has NO unique (product_id, key) index, so two rows
    // with different values are legal. Ambiguous -> unset. Do NOT "take the first"
    // (the idiom the neighbouring min/max_deposit_amount `find()` calls use).
    if (rows.length !== 1) {
        return undefined;
    }

    let value: unknown = rows[0].value;
    // A JSON round-trip may deliver a scalar bare or single-element-array-wrapped.
    // Only a single-element array is unambiguous.
    if (Array.isArray(value)) {
        if (value.length !== 1) {
            return undefined;
        }
        value = value[0];
    }
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
        value = Number(value.trim());
    }
    if (typeof value !== 'number' || !Number.isInteger(value)) {
        return undefined;
    }
    if (value < range.min || value > range.max) {
        return undefined;
    }
    return value;
}

/** Whole months by which the DD collection window must finish before
 *  the final service falls due. Consumed by PaymentStep's item-based family. */
export function parseCollectionLeadMonths(settings: ConfigSetting[]): number | undefined {
    return parseSingleIntegerSetting(settings, 'collection_lead_months', LEAD_RANGE);
}

/**
 * Fail-closed read-time resolution of the DEALER-OVERRIDE window for
 * `collection_lead_months` (#728, Slice B). ADVISORY ONLY: it decides whether
 * `CollectionLeadInput` is offered on the payment step and what its Zod bound
 * is. The AUTHORITATIVE bound is server-side, in quote-service —
 * `CollectionLeadBoundsClient::parseBounds`
 * (backend/quote-service/app/Services/CollectionLeadBoundsClient.php:138-198),
 * enforced on POST/PATCH /api/quotes. This function must agree with it
 * case-for-case; productTransform.test.ts mirrors CollectionLeadBoundsClientTest
 * by name so the two lists can be diffed.
 *
 * The trio is `min_collection_lead_months` / `collection_lead_months` /
 * `max_collection_lead_months`, resolved together or not at all under
 * `0 <= min <= default <= max <= MAX_COLLECTION_LEAD_MONTHS`. The CENTRE is
 * read by REUSING `parseCollectionLeadMonths` above, not by re-implementing
 * it — so the value that drives today's schedule and the value that anchors
 * these bounds cannot diverge, which is the single strongest drift control
 * available client-side.
 *
 * Returns `{ min, max }` and NOT the default: the default is already
 * `Product.collectionLeadMonths`, and the coherence invariant guarantees it
 * sits inside the window whenever this resolves. Same shape as the server's
 * `CollectionLeadBounds` value object, deliberately.
 *
 * `null` means THE OVERRIDE IS SWITCHED OFF for this product — never "use a
 * default bound", never "clamp", never "take the first row". `PaymentStep`
 * then renders no control at all and the product behaves byte-identically to
 * #727.
 *
 *   RESOLVES        all three rows present exactly once, integer-strict,
 *                   non-negative, coherent, max <= MAX_COLLECTION_LEAD_MONTHS
 *   null            no trio key at all; ANY key missing; ANY key duplicated;
 *                   any member non-integer / decimal / negative / NaN /
 *                   Infinity / array-ambiguous; min > max; default outside
 *                   [min, max]; max > MAX_COLLECTION_LEAD_MONTHS
 *
 * `min` and `max` are read by `parseLeadBoundMember` below, which applies
 * `parseCollectionLeadMonths`' posture VERBATIM (one row, single-element
 * unwrap, trimmed all-digits coercion, integer-strict,
 * `0 <= v <= MAX_COLLECTION_LEAD_MONTHS`) — the three members of the trio are
 * therefore parsed identically to EACH OTHER, which matters more than
 * matching the slightly different idiom `resolveServiceCountMode.ts` uses in
 * another feature.
 */
export function parseCollectionLeadBounds(
    settings: ConfigSetting[],
): { min: number; max: number } | null {
    // The centre check subsumes integrality, non-negativity and the <= 6
    // ceiling on the default (B2) — the coherence check below reduces to
    // min <= default && default <= max, since parseLeadBoundMember already
    // restricts min/max to the SAME [0, MAX_COLLECTION_LEAD_MONTHS] range.
    const defaultValue = parseCollectionLeadMonths(settings);
    if (defaultValue === undefined) {
        return null;
    }

    const min = parseLeadBoundMember(settings, 'min_collection_lead_months');
    const max = parseLeadBoundMember(settings, 'max_collection_lead_months');
    if (min === undefined || max === undefined) {
        return null;
    }

    if (min > max || defaultValue < min || defaultValue > max) {
        return null;
    }

    return { min, max };
}

/** Private wrapper over `parseSingleIntegerSetting` for the lead trio's
 *  bound keys, same `[0, MAX_COLLECTION_LEAD_MONTHS]` range as the centre. */
function parseLeadBoundMember(settings: ConfigSetting[], key: string): number | undefined {
    return parseSingleIntegerSetting(settings, key, LEAD_RANGE);
}

/** A malformed or ambiguous row resolves to unset, never a narrower floor —
 *  it must never remove a sellable method the salesperson could otherwise choose. */
export function parseMinCollectionInstalments(settings: ConfigSetting[]): number | undefined {
    return parseSingleIntegerSetting(settings, 'min_collection_instalments', INSTALMENTS_RANGE);
}

/** A malformed row resolves to unset — the plan term alone decides — never
 *  a substituted default that a schedule could be priced over. */
export function parseDefaultCollectionInstalments(settings: ConfigSetting[]): number | undefined {
    return parseSingleIntegerSetting(settings, 'default_collection_instalments', INSTALMENTS_RANGE);
}

/** 2.4.7: the upper bound offered by the Number of Collections control. Malformed/unset resolves
 *  to undefined — no configured ceiling beyond the term and elemis's transport max. */
export function parseMaxCollectionInstalments(settings: ConfigSetting[]): number | undefined {
    return parseSingleIntegerSetting(settings, 'max_collection_instalments', INSTALMENTS_RANGE);
}

/** Fail-closed boolean config row (one row, real JSON boolean, scalar or single-element array). */
function parseBooleanSetting(settings: ConfigSetting[], key: string): boolean {
    const rows = settings.filter((s) => s.key === key);
    if (rows.length !== 1) return false;
    let value: unknown = rows[0].value;
    if (Array.isArray(value)) {
        if (value.length !== 1) return false;
        value = value[0];
    }
    return value === true;
}

/** 2.4.7: whether the Number of Collections control is offered. Fail-closed, like `allow_deposit`. */
export function parseAllowCollectionInstalmentChoice(settings: ConfigSetting[]): boolean {
    return parseBooleanSetting(settings, 'allow_collection_instalment_choice');
}

/** The auto-selected scheduling method on a fresh quote. A single non-empty string row; its
 *  membership of the offered methods is checked downstream (`resolveDefaultMethodKey`), so a
 *  stale/unknown key simply falls back to the first offerable method. */
export function parseDefaultSchedule(settings: ConfigSetting[]): string | undefined {
    const rows = settings.filter((s) => s.key === 'default_schedule');
    if (rows.length !== 1) {
        return undefined;
    }
    let value: unknown = rows[0].value;
    if (Array.isArray(value)) {
        if (value.length !== 1) {
            return undefined;
        }
        value = value[0];
    }
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/** Fail-closed: a missing, duplicated or non-boolean row means NO deposit is offered — never
 *  true by inference. No string coercions: the admin editor writes a real JSON boolean only. */
export function parseAllowDeposit(settings: ConfigSetting[]): boolean {
    const rows = settings.filter((s) => s.key === 'allow_deposit');
    if (rows.length !== 1) return false;
    let value: unknown = rows[0].value;
    // A JSON round-trip may deliver a scalar bare or single-element-array-wrapped.
    if (Array.isArray(value)) {
        if (value.length !== 1) return false;
        value = value[0];
    }
    return value === true;
}

export function isMoneyJson(v: unknown): v is MoneyJson {
    return (
        typeof v === 'object' && v !== null
        && 'amount' in v && typeof (v as Record<string, unknown>).amount === 'string'
        && 'currency' in v && typeof (v as Record<string, unknown>).currency === 'string'
        && 'minor' in v && typeof (v as Record<string, unknown>).minor === 'number'
    );
}

/** Undefined when unset, duplicated or malformed — the input then has no prefill and cannot be
 *  fixed by a default. Never a substituted zero. */
export function parseDefaultDepositAmountMinor(settings: ConfigSetting[]): number | undefined {
    const rows = settings.filter((s) => s.key === 'default_deposit_amount');
    if (rows.length !== 1 || !isMoneyJson(rows[0].value)) return undefined;
    // `isMoneyJson` accepts any `number` minor, but quote-service rules this key
    // `nullable|integer|min:0` — a decimal or negative would 422 the next quote save.
    const { minor } = rows[0].value as MoneyJson;
    return Number.isInteger(minor) && minor >= 0 ? minor : undefined;
}

const DISPLAY_AMOUNT = /^\d+\.\d{2}$/;
const CURRENCY_CODE = /^[A-Z]{3}$/;

/** Undefined when unset, duplicated or malformed; the card then shows no price line. */
export function parseFeaturePriceFrom(settings: ConfigSetting[]): string | undefined {
    const rows = settings.filter((s) => s.key === 'feature_price_from');
    if (rows.length !== 1 || !isMoneyJson(rows[0].value)) return undefined;
    const { amount, currency, minor } = rows[0].value as MoneyJson;
    if (!Number.isSafeInteger(minor) || minor < 0) return undefined;
    if (!DISPLAY_AMOUNT.test(amount) || !CURRENCY_CODE.test(currency)) return undefined;
    if (displayAmountToMinor(amount) !== BigInt(minor)) return undefined;
    const currencySymbol = currency === 'GBP' ? '£' : currency;
    return `From ${currencySymbol}${amount}`;
}

/** Converts a validated `DISPLAY_AMOUNT` string to pence exactly; `Number` loses precision above 2^53. */
function displayAmountToMinor(amount: string): bigint {
    const [pounds, pence] = amount.split('.');
    return BigInt(pounds) * 100n + BigInt(pence);
}

/**
 * Normalize a raw config setting: rename `config_option` → `configOption`
 * to match the frontend type contract.
 */
function normalizeConfigSetting(raw: RawConfigSetting): ConfigSetting {
    const configOption = raw.configOption ?? raw.config_option ?? null;
    return {
        id: raw.id ?? '',
        product_id: raw.product_id ?? '',
        config_option_id: raw.config_option_id ?? null,
        key: raw.key,
        value: raw.value as ConfigSetting['value'],
        configOption,
        created_at: raw.created_at,
        updated_at: raw.updated_at,
    };
}

/**
 * Coerce a raw features-row value into one or more string bullets.
 *
 * Historically a single `features` row could store a JSON-encoded array
 * (legacy seeder shape pre-#261). New code emits one row per bullet.
 * A data migration was added in #285 to split legacy rows into multiple
 * single-string rows, but this defensive coercion is kept so any
 * environment that has not yet run that migration still renders
 * sensibly instead of showing a JSON-looking string.
 */
function expandFeatureRow(value: unknown): string[] {
    if (Array.isArray(value)) return value.map((v) => String(v));
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) return parsed.map((v) => String(v));
            } catch {
                // fall through to plain string
            }
        }
        return [value];
    }
    return [String(value)];
}

export const transformApiProduct = (apiProduct: RawApiProduct): Product => {
    const rawSettings = apiProduct.config_settings ?? [];
    const normalizedSettings: ConfigSetting[] = rawSettings.map(normalizeConfigSetting);

    // features: multi-row, preserve server-side insertion order (created_at ASC, then id).
    // Backend ordering is enforced by `Product::config_settings()` relation ordering.
    const features: string[] = normalizedSettings
        .filter((s) => s.key === 'features')
        .flatMap((s) => expandFeatureRow(s.value));

    // min_deposit_amount: single row, value is MoneyResource JSON shape
    const depositRow = normalizedSettings.find((s) => s.key === 'min_deposit_amount');
    const depositMoney = isMoneyJson(depositRow?.value) ? depositRow?.value as MoneyJson : undefined;

    // max_deposit_amount (#621, generic): single row, value is MoneyResource JSON shape.
    // Undefined when unset/malformed — each deposit-bearing method stays bounded only
    // by its own natural ceiling (the quote total for deposit_plus_even, the first
    // collection target for item_based_with_deposit).
    const maxDepositRow = normalizedSettings.find((s) => s.key === 'max_deposit_amount');
    const maxDepositMoney = isMoneyJson(maxDepositRow?.value) ? maxDepositRow?.value as MoneyJson : undefined;

    return {
        ...apiProduct,
        status: apiProduct.status,
        deleted_at: apiProduct.deleted_at ?? null, // "Retired" in the UI
        effective_from: apiProduct.effective_from?.slice(0, 10) ?? null,
        config_settings: normalizedSettings,
        features,
        // Normalise capabilities: backend returns relation objects; FE contract is string[] of keys.
        capabilities: Array.isArray(apiProduct.capabilities)
            ? (apiProduct.capabilities as Array<{ key: string }>).map((c) => c.key)
            : [],
        // scheduling_methods (snake_case, #474) -> schedulingMethods (camelCase).
        // No config / relation not loaded resolves to [] server-side already;
        // guard defensively here too so a missing/malformed field never throws.
        schedulingMethods: Array.isArray(apiProduct.scheduling_methods)
            ? apiProduct.scheduling_methods
            : [],
        // Minor-unit minimum deposit, so `DepositInput` can validate against it
        // without re-deriving it.
        minDepositAmountMinor: depositMoney?.minor,
        // #621: GENERIC deposit CEILING (minor units). Undefined = no cap (each method
        // keeps its natural ceiling only). Consumed for EVERY deposit-bearing method in
        // PaymentStep — the symmetric upper bound to min_deposit_amount's lower bound.
        maxDepositAmountMinor: maxDepositMoney?.minor,
        // #727: whole months by which the DD collection window must finish before the
        // final service falls due. Undefined = unset = today's behaviour (collection
        // runs to the final service date). Consumed by PaymentStep for the item-based
        // family; see the plan's D7 for why even_monthly/deposit_plus_even are not yet
        // covered.
        collectionLeadMonths: parseCollectionLeadMonths(normalizedSettings),
        // #728, Slice B: the per-product dealer-override window for the lead
        // above. null = no override offered (the default for every product
        // today) — see parseCollectionLeadBounds. ADVISORY; quote-service
        // enforces the same trio authoritatively on save.
        collectionLeadBounds: parseCollectionLeadBounds(normalizedSettings),
        // #750: the fewest monthly instalments a DERIVED even-monthly schedule may be
        // collected over. Undefined = unset = no constraint beyond elemis's transport
        // minimum of 1 (today's behaviour). A GATE on whether the method is offered —
        // it never lengthens a schedule. Consumed by PaymentStep for the even-monthly
        // family only.
        minCollectionInstalments: parseMinCollectionInstalments(normalizedSettings),
        maxCollectionInstalments: parseMaxCollectionInstalments(normalizedSettings),
        allowCollectionInstalmentChoice: parseAllowCollectionInstalmentChoice(normalizedSettings),
        defaultSchedule: parseDefaultSchedule(normalizedSettings),
        fees: parseProductFees(apiProduct.product_fees),
        // Even-monthly count sent: min(default, term - lead), or the default alone when no term exists.
        // Unset means the term alone decides, byte-identical to before this setting.
        defaultCollectionInstalments: parseDefaultCollectionInstalments(normalizedSettings),
        // Whether this product's scheduled methods carry a deposit at all — fail-closed, see
        // parseAllowDeposit.
        allowDeposit: parseAllowDeposit(normalizedSettings),
        // The deposit-step prefill and editability input (S1b) — undefined unless exactly one
        // row carries a MoneyJson value. See resolveDepositMode for how this is consumed.
        defaultDepositAmountMinor: parseDefaultDepositAmountMinor(normalizedSettings),
        price: parseFeaturePriceFrom(normalizedSettings),
        uuid: apiProduct.id, // For carousel compatibility
        highlight: false, // Default value
    } as Product;
};
